import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import BuildSegmentModal from '../components/segments/BuildSegmentModal';
import { formatDistanceToNow } from '../utils/dateUtils';

export default function SegmentsPage() {
  const {
    segments,
    segmentsLoading,
    segmentsError,
    addSegment,
    updateSegment,
    deleteSegment,
    generateSegmentDefinition,
    getSegmentStatistics,
    currentUser,
  } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentStats, setSegmentStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(new Set());
  const requestedStatsRef = useRef(new Set());

  const handleCreate = () => {
    setEditingSegment(null);
    setIsModalOpen(true);
  };

  const handleEdit = (segment) => {
    setEditingSegment(segment);
    setIsModalOpen(true);
  };

  const handleDelete = async (segment) => {
    const confirmed = typeof window !== 'undefined'
      ? window.confirm(`Delete segment "${segment.name}"?`)
      : true;
    if (confirmed) {
      try {
        await deleteSegment(segment.uuid);
        setSegmentStats(prev => {
          const next = { ...prev };
          delete next[segment.uuid];
          return next;
        });
        requestedStatsRef.current.delete(segment.uuid);
      } catch (error) {
        console.error('Failed to delete segment', error);
        if (typeof window !== 'undefined') {
          window.alert('Failed to delete segment. Please try again.');
        }
      }
    }
  };

  const handleSave = async (payload) => {
    try {
      if (editingSegment) {
        const updated = await updateSegment(editingSegment.uuid, {
          ...payload,
          updatedAt: new Date().toISOString()
        });
        if (updated?.uuid) {
          requestedStatsRef.current.delete(updated.uuid);
          loadSegmentStatistics(updated.uuid);
        }
      } else {
        const created = await addSegment({
          ...payload,
          createdBy: currentUser || 'admin'
        });
        if (created?.uuid) {
          loadSegmentStatistics(created.uuid);
        }
      }
      setIsModalOpen(false);
      setEditingSegment(null);
    } catch (error) {
      console.error('Failed to save segment', error);
      if (typeof window !== 'undefined') {
        window.alert('Failed to save segment. Please try again.');
      }
    }
  };

  const loadSegmentStatistics = useCallback(async (segmentUuid) => {
    if (!segmentUuid || loadingStats.has(segmentUuid) || requestedStatsRef.current.has(segmentUuid)) {
      return;
    }

    requestedStatsRef.current.add(segmentUuid);
    setLoadingStats(prev => new Set(prev).add(segmentUuid));
    try {
      const stats = await getSegmentStatistics(segmentUuid);
      setSegmentStats(prev => ({
        ...prev,
        [segmentUuid]: stats
      }));
    } catch (error) {
      console.error(`Failed to load statistics for segment ${segmentUuid}`, error);
      setSegmentStats(prev => ({
        ...prev,
        [segmentUuid]: { vasts: 0, totalImpressions: 0 }
      }));
    } finally {
      setLoadingStats(prev => {
        const next = new Set(prev);
        next.delete(segmentUuid);
        return next;
      });
    }
  }, [getSegmentStatistics, loadingStats]);

  useEffect(() => {
    if (segments.length > 0 && !segmentsLoading) {
      segments.forEach((segment, index) => {
        if (segment.uuid && !requestedStatsRef.current.has(segment.uuid)) {
          setTimeout(() => {
            loadSegmentStatistics(segment.uuid);
          }, index * 50);
        }
      });
    }
  }, [segments, segmentsLoading]);

  return (
    <section className="content-section active">
      <div className="section-header">
        <div>
          <h2>Audience Segments</h2>
          <p>Discover granular audiences ready for activation</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>+ Build Segment</button>
      </div>

      {segmentsError && (
        <div className="empty-state">
          <h3>Unable to load segments</h3>
          <p>Please refresh the page or try again later.</p>
        </div>
      )}

      {segmentsLoading && segments.length === 0 ? (
        <div className="empty-state">
          <h3>Loading segments…</h3>
          <p>Fetching segment definitions from the server.</p>
        </div>
      ) : (
        <div className="segments-list">
          {segments.map(segment => {
            const lastUpdated = segment.updatedAt || segment.createdAt;
            const relativeUpdated = lastUpdated ? formatDistanceToNow(lastUpdated) : '';
            return (
              <article key={segment.uuid} className="segment-card">
                <div className="segment-header">
                  <div>
                    <h3 className="segment-title">{segment.name}</h3>
                    <p className="segment-description">{segment.description}</p>
                    <p className="segment-meta">
                      {relativeUpdated ? `Updated ${relativeUpdated} ago` : 'Updated just now'} ·{' '}
                      Created by {segment.createdBy || 'admin'}
                    </p>
                  </div>
                  <div className="segment-actions">
                    <button
                      className="btn-icon"
                      type="button"
                      aria-label={`Edit ${segment.name}`}
                      onClick={() => handleEdit(segment)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon danger"
                      type="button"
                      aria-label={`Delete ${segment.name}`}
                      onClick={() => handleDelete(segment)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="segment-details">
                  <div className="detail-box">
                    <div className="detail-label">Condition Overview</div>
                    <div className="segment-condition-summary">
                      <span>{segment.definition?.all?.length ?? 0} must match</span>
                      <span>{segment.definition?.any?.length ?? 0} optional</span>
                      <span>{segment.definition?.none?.length ?? 0} exclusions</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div className="detail-box">
                      <div className="detail-label">Unique Devices (Last month)</div>
                      <div className="segment-vasts-count">
                        {loadingStats.has(segment.uuid) ? (
                          <span style={{ opacity: 0.6 }}>Loading...</span>
                        ) : (
                          (segmentStats[segment.uuid]?.vasts ?? segment.vasts ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                    <div className="detail-box">
                      <div className="detail-label">Total Impressions (Last month)</div>
                      <div className="segment-vasts-count">
                        {loadingStats.has(segment.uuid) ? (
                          <span style={{ opacity: 0.6 }}>Loading...</span>
                        ) : (
                          (segmentStats[segment.uuid]?.totalImpressions ?? segment.totalImpressions ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <BuildSegmentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSegment(null);
        }}
        initialSegment={editingSegment}
        onSave={handleSave}
        onGenerateDefinition={generateSegmentDefinition}
      />
    </section>
  );
}
